/**
 * ContractTab — supplier contract docs checklist + e-signature envelope.
 *
 * Borrows the existing ContractDocsPanel for the checklist and adds
 * the SignatureFlow envelope viewer. Visual-only PDF preview of the
 * current contract version on the right.
 */
import React from 'react';
import ContractDocsPanel from '../components/ContractDocsPanel';
import SignatureFlow from '../components/SignatureFlow';
import PdfPreview from '../components/PdfPreview';
import { useManifest } from '../ManifestContext';
import { getOrderMocks } from '../mocks';
import { FileSignature } from 'lucide-react';

const ContractTab = () => {
  const { activeOrder } = useManifest();
  const mocks = getOrderMocks(activeOrder.id);
  const contractEnvelope = mocks.signatures.find((s) => s.title.toLowerCase().includes('sales contract'));
  const factory = activeOrder.factories?.find((f) => f.id === activeOrder.pickedQuoteFactoryId);
  const quote = activeOrder.quotes?.find((q) => q.factoryId === activeOrder.pickedQuoteFactoryId);

  return (
    <div className="manifest-tab-pane">
      <ContractDocsPanel order={activeOrder} />

      <div className="manifest-section-head" style={{ marginTop: '20px' }}>
        <h3><FileSignature size={16} /> E-signature</h3>
        <span>DocuSign-equivalent flow, embedded</span>
      </div>

      <div className="contract-tab-grid">
        <div>
          {contractEnvelope ? (
            <SignatureFlow envelope={contractEnvelope} />
          ) : (
            <div className="manifest-empty manifest-empty-inline">
              <p>No signature envelope yet. Generate the supplier contract first.</p>
              <button className="manifest-btn-primary-sm">Send contract for signature</button>
            </div>
          )}
        </div>
        <div>
          <PdfPreview
            title="Sales Contract"
            fileName={contractEnvelope?.docName || `sales-contract-draft.pdf`}
            badge={contractEnvelope?.status === 'completed' ? 'Signed' : 'Draft'}
            watermark={contractEnvelope?.status === 'completed' ? null : 'DRAFT'}
          >
            <div className="pdf-meta-block">
              <div><strong>Buyer:</strong> [AI]Quire Procurement Inc.</div>
              <div><strong>Seller:</strong> {factory?.name || 'Pending selection'}</div>
              <div><strong>Country:</strong> {factory?.country || '—'}</div>
              <div><strong>Contract value:</strong> ${activeOrder.contractValueCad?.toLocaleString()} CAD</div>
              <div><strong>Quantity:</strong> {activeOrder.quantity?.toLocaleString()} units</div>
              <div><strong>Unit price:</strong> ${quote?.unitPriceUsd || '—'} USD</div>
              <div><strong>Incoterm:</strong> {quote?.incoterm || '—'}</div>
              <div><strong>Lead time:</strong> {quote?.leadTimeDays || '—'} days from PO</div>
            </div>
            <div className="pdf-divider" />
            <div className="pdf-spec-section">
              <div className="pdf-spec-heading">1. Scope of supply</div>
              <div className="pdf-spec-body">{activeOrder.productSummary}</div>
            </div>
            <div className="pdf-spec-section">
              <div className="pdf-spec-heading">2. Payment terms</div>
              <div className="pdf-spec-body">30% deposit on PO; 70% balance against B/L copy. Trade Assurance protected.</div>
            </div>
            <div className="pdf-spec-section">
              <div className="pdf-spec-heading">3. Quality + warranty</div>
              <div className="pdf-spec-body">Goods to meet certifications enumerated in Appendix A (technical specification sheet). 12-month manufacturer warranty.</div>
            </div>
            <div className="pdf-spec-section">
              <div className="pdf-spec-heading">4. Delivery</div>
              <div className="pdf-spec-body">Goods to be delivered to {activeOrder.deliveryRegion}, on or before contract deadline.</div>
            </div>
            <div className="pdf-footer">
              {contractEnvelope?.status === 'completed' ? '◇ Signed by both parties — see envelope details.' : '◇ Draft — pending negotiation and signature.'}
            </div>
          </PdfPreview>
        </div>
      </div>
    </div>
  );
};

export default ContractTab;
